import { useAlerts } from "components/Alerts/AlertProvider";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useMoveToReview, useUpdateSubmission } from "hooks/useSubmissionMutations";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useCallback } from "react";

import { useDroppedApprovalGate } from "./useDroppedApprovalGate";
import {
  useReviewerConfigConfirmHandlers,
  useReviewerConfigHandlers,
} from "./useReviewerConfigHandlers";
import { useReviewerPreparationConfigState } from "./useReviewerPreparationConfigState";
import { useReviewerPreparationContext } from "./useReviewerPreparationContext";
import { useReviewerPreparationDialogState } from "./useReviewerPreparationDialogState";
import { useReviewerPreparationSaveHandlers } from "./useReviewerPreparationSaveHandlers";
import { useReviewerPreparationSendHandlers } from "./useReviewerPreparationSendHandlers";
import {
  useReviewerPreparationIssueBreakdown,
  useReviewerPreparationValidation,
} from "./useReviewerPreparationValidation";
import { useReviewerStatusOverrideHandlers } from "./useReviewerStatusOverrideHandlers";

/** Reviewer/signer/signature config plus the validation, save, and send behavior for the Reviewer Preparation step. */
export function useReviewerPreparationState(submissionId: string) {
  const alerts = useAlerts();
  const [moveToReview, { loading: sending }] = useMoveToReview();
  const [updateSubmission, { loading: saving }] = useUpdateSubmission();
  const { touchStep } = useTouchSubmissionMutation(submissionId);
  const { goToNextStep } = useStepperNavigation();

  const context = useReviewerPreparationContext();
  const configState = useReviewerPreparationConfigState(context);
  const dialogState = useReviewerPreparationDialogState();

  const validation = useReviewerPreparationValidation({ ...context, ...configState, submissionId });
  const issueBreakdown = useReviewerPreparationIssueBreakdown({
    ...context,
    ...configState,
    displayIssues: validation.displayIssues,
  });

  const configHandlers = useReviewerConfigHandlers({ ...context, ...configState, ...dialogState });
  const confirmHandlers = useReviewerConfigConfirmHandlers({
    ...configState,
    ...dialogState,
    applyReviewersSave: configHandlers.applyReviewersSave,
  });
  const statusOverrideHandlers = useReviewerStatusOverrideHandlers({
    ...configState,
    ...dialogState,
    updateSubmission,
    submissionId,
    alerts,
  });

  const saveHandlers = useReviewerPreparationSaveHandlers({
    ...configState,
    ...dialogState,
    updateSubmission,
    submissionId,
    alerts,
    reviewerInputs: validation.reviewerInputs,
    revalidate: validation.revalidate,
  });
  const sendHandlers = useReviewerPreparationSendHandlers({
    ...configState,
    ...dialogState,
    moveToReview,
    touchStep,
    goToNextStep,
    submissionId,
    alerts,
    reviewerInputs: validation.reviewerInputs,
    revalidate: validation.revalidate,
    reviewersToPersistCount: configState.reviewersToPersist.length,
    reviewersCount: configState.reviewers.length,
    issuesCount: validation.issues.length,
  });

  // Save and Send are the only two paths that write the reviewer list, so the
  // "this drops an approval" confirm wraps both rather than each control.
  const guardDroppedApprovals = useDroppedApprovalGate({
    persistedReviewers: context.persistedReviewers,
    reviewerInputs: validation.reviewerInputs,
    setDroppedApprovalsToConfirm: dialogState.setDroppedApprovalsToConfirm,
  });

  const handleSaveConfig = useCallback(
    () => guardDroppedApprovals(() => void saveHandlers.handleSaveConfig()),
    [guardDroppedApprovals, saveHandlers]
  );
  const handleSend = useCallback(
    () => guardDroppedApprovals(() => void sendHandlers.handleSend()),
    [guardDroppedApprovals, sendHandlers]
  );
  const handleConfirmDroppedApprovals = useCallback(() => {
    const pending = dialogState.droppedApprovalsToConfirm;
    dialogState.setDroppedApprovalsToConfirm(null);
    pending?.proceed();
  }, [dialogState]);

  return {
    ...context,
    ...configState,
    ...dialogState,
    ...validation,
    ...issueBreakdown,
    ...configHandlers,
    ...confirmHandlers,
    ...statusOverrideHandlers,
    ...saveHandlers,
    ...sendHandlers,
    handleSaveConfig,
    handleSend,
    handleConfirmDroppedApprovals,
    saving,
    sending,
  };
}
